/**
 * Bloque **DINERO** de la ficha de cliente (2026-08-10).
 *
 * Antes de esto, la ficha del cliente no sabía que el cliente debía dinero:
 * no importaba nada de recobros. El escenario que arregla es concreto y
 * frecuente — el cliente llama enfadado por una carta de impago, la operadora
 * abre su ficha y no ve ni la deuda, ni el caso, ni qué se le ha enviado, así
 * que salta entre tres pantallas mientras el cliente espera al teléfono.
 *
 * Se apoya en lo que YA está servido: la búsqueda de episodios y la de tareas
 * aceptan el identificador del cliente, así que no hizo falta endpoint nuevo.
 *
 * DEGRADA EN SILENCIO: si ms-ops no está configurado en el entorno, o el
 * cliente no tiene ningún caso, el bloque no se pinta. La ficha nunca se rompe
 * por esto — es información añadida, no un requisito.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { listEpisodes, listTasks, useMsOpsEnabled } from "../../../api/msops";
import type { EpisodeListItem, TaskListItem } from "../../../types/msops";

const COLORS = {
  heading: "#212529",
  muted: "#6c757d",
  border: "#dee2e6",
  link: "#0d6efd",
};

const TPL_LABEL: Record<string, string> = {
  "relance-1-impaye-d2c-fr": "relance 1",
  "mise-en-demeure-d2c-fr": "mise en demeure",
  "rappel-impaye-d2c-fr": "rappel",
  "suspension-garanties-d2c-fr": "suspensión",
  "resiliation-definitive-d2c-fr": "résiliation",
};

function ddmm(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function DunningCard({ leadUlid }: { leadUlid: string }) {
  const { token } = useAuth();
  const msOpsEnabled = useMsOpsEnabled();
  const [episodes, setEpisodes] = useState<EpisodeListItem[] | null>(null);
  const [tasks, setTasks] = useState<TaskListItem[]>([]);

  useEffect(() => {
    if (!msOpsEnabled || !leadUlid) return;
    let alive = true;
    // Las dos consultas van en paralelo y toleran fallo por separado: media
    // ficha es mejor que ninguna cuando alguien está esperando al teléfono.
    Promise.allSettled([
      listEpisodes({ q: leadUlid, pageSize: 5 }, { token }),
      listTasks({ q: leadUlid, status: "pending", pageSize: 5 }, { token }),
    ]).then(([eps, tks]) => {
      if (!alive) return;
      setEpisodes(eps.status === "fulfilled" ? eps.value.items : []);
      setTasks(tks.status === "fulfilled" ? tks.value.items : []);
    });
    return () => {
      alive = false;
    };
  }, [leadUlid, token, msOpsEnabled]);

  // Sin ms-ops, mientras carga, o sin ningún caso: el bloque no existe.
  if (!msOpsEnabled || episodes === null || episodes.length === 0) return null;

  const open = episodes.filter((e) => e.status === "open");

  return (
    <section
      style={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: ".5rem",
        background: "#fff",
        padding: ".85rem 1rem",
        marginBottom: "0.75rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: ".6rem", marginBottom: ".5rem" }}>
        <h2 style={{ fontSize: "1rem", margin: 0, color: COLORS.heading }}>Recobros</h2>
        <Link
          to="/operations/recobros/cartera"
          style={{ color: COLORS.link, fontSize: ".78rem", marginLeft: "auto" }}
        >
          Ver deuda →
        </Link>
      </div>

      {open.length === 0 && (
        <p style={{ color: COLORS.muted, fontSize: ".85rem", margin: 0 }}>
          Sin impagos en curso. Tuvo {episodes.length} caso(s) cerrado(s).
        </p>
      )}

      {open.map((ep) => {
        const task = tasks.find((t) => t.episodeId === ep.id);
        return (
          <div
            key={ep.id}
            style={{
              display: "grid",
              gap: ".2rem",
              paddingTop: ".4rem",
              borderTop: `1px solid ${COLORS.border}`,
              marginTop: ".4rem",
              fontSize: ".85rem",
            }}
          >
            <div>
              <strong>{ep.pendingInvoicesCount} recibo(s) impagados</strong>
              {ep.disputed && (
                <span style={{ color: "#856404", marginLeft: ".4rem" }}>· en disputa</span>
              )}
              {ep.subscriptionRef && (
                <span style={{ color: COLORS.muted }}> · póliza {ep.subscriptionRef}</span>
              )}
            </div>
            <div style={{ color: COLORS.muted }}>
              {task ? (
                <>
                  Pendiente: <strong>{TPL_LABEL[task.templateId] ?? task.templateId}</strong>{" "}
                  {task.overdue ? "· VENCIDA" : `· vence ${ddmm(task.dueAt)}`}
                </>
              ) : ep.lastCommunication ? (
                <>
                  Última carta:{" "}
                  <strong>
                    {TPL_LABEL[ep.lastCommunication.templateId ?? ""] ??
                      ep.lastCommunication.templateId ??
                      "—"}
                  </strong>{" "}
                  · {ddmm(ep.lastCommunication.sentAt ?? ep.lastCommunication.dueAt)}
                </>
              ) : (
                <>Sin comunicaciones todavía</>
              )}
            </div>
            <div style={{ display: "flex", gap: ".7rem" }}>
              <Link to={`/operations/recobros/${ep.id}`} style={{ color: COLORS.link }}>
                Ver caso →
              </Link>
              {task && (
                <Link
                  to={`/operations/recobros/tareas?episodeId=${ep.id}`}
                  style={{ color: COLORS.link }}
                >
                  Ir a la tarea →
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
